// Controller hook for the agentic ai-assistant backend (MINSKY-16473 / MINSKY-16479 —
// frontend-integration half). MINSKY-16657 mounts this hook into the real
// `AIAssistantChatPanel.jsx`. That panel is the UI built on top of this hook.
//
// Per the locked architecture decision, the plan itself (the raw `Plan`/`PlanStep[]` JSON from
// `planning-completed`) is NEVER surfaced here — only human-readable per-step progress/reasoning
// derived from it (tool name + status, brief planning/replanning narration).
//
// MINSKY-16657 conversation-continuity note: this hook (not the backend) mints the
// conversationId, via `generateConversationId()`, and sends it on EVERY `POST /runs` call from the
// very first turn onward (see `submitGoal`) — this is deliberate so the id this hook tracks always
// matches the id MINSKY-16655's `persist-run-completion.ts` actually writes to Postgres, letting
// `loadConversation`/history (MINSKY-16656's `GET /conversations/:id`) round-trip reliably. A
// caller-supplied `conversationId` (e.g. from a URL param or `ThreadHistory` selection) always
// takes precedence — this hook only generates its own id when starting a genuinely new thread.
//
// Scrollback follow-up (direct bugfix on MINSKY-16657's shipped scope, no new ticket): the
// top-level `goal`/`steps`/`reasoningLog`/`finalAnswerText`/`citations`/`status`/`error`/
// `runId` fields still describe ONLY the current/live turn (in-flight or just-finished) — that
// contract is unchanged so existing consumers of those fields keep working. What changed is that
// prior turns are no longer discarded: `history` (a new array field) accumulates every PAST
// exchange for the active thread, oldest-first, so the UI can render a full scrollback with the
// live turn appended at the end instead of wiping everything down to the latest exchange on every
// `submitGoal`/`loadConversation`.
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    startAgenticRun,
    getAgenticConversation,
    getChartAiConfig,
} from '../services/agenticRun/agenticRunApi';
import {streamAgenticRun} from '../services/agenticRun/streamAgenticRun';
import {isRecoverableConversationLoadError} from '../utils/chatMessageUtils';
import {
    readPersistedMutationPreview,
    writePersistedMutationPreview,
} from '../utils/mutationPreviewPersistence';
import useChartAiBridgeStore from '../../../stores/useChartAiBridgeStore';
import {stripChartOpsFromStreamingText} from './stripChartOpsFromStreamingText';
import {parseChartMutationMarkup} from '../../whiteboards/ai/parseChartMutationMarkup';
import {
    buildContextSuggestDismissGoal,
    buildContextSuggestLinkGoal,
    normalizeContextSuggestions,
    resolveContextSuggestions,
} from './parseContextSuggestions';
import {formatSearchQueries, labelForProgressPhase} from './pureLlmReasoningLabels';

export const AGENTIC_RUN_STATUS = {
    IDLE: 'idle',
    STARTING: 'starting',
    RUNNING: 'running',
    COMPLETED: 'completed',
    FAILED: 'failed',
};

const REMOVE_CHART_OP_RE = /<chart-op\b[^>]*\btype\s*=\s*["'](?:remove-entity|delete-entity|remove-arrow|delete-arrow)["']/i;

function markupHasRemoveOps(markup) {
    return Boolean(markup && REMOVE_CHART_OP_RE.test(markup));
}

function markupOpStatusesEqual(left, right) {
    if (left === right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) {
        return false;
    }
    return leftKeys.every((key) => left[key] === right[key]);
}

const CHART_OP_TAG_RE = /<chart-op\b[^>]*?\/?\s*>/gi;

function extractChartOpTags(markup) {
    if (!markup || typeof markup !== 'string') {
        return [];
    }
    return markup.match(CHART_OP_TAG_RE) || [];
}

/**
 * Stable identity for a parsed chart-op — used to union enrichment re-emits
 * without duplicating the early-batch ops.
 * @param {object|null|undefined} op
 * @returns {string}
 */
function markupOpIdentity(op) {
    if (!op || typeof op !== 'object') {
        return '';
    }
    if (op.type === 'add-entity' || op.type === 'create-local' || op.type === 'remove-entity') {
        return `${op.type}:${op.ref || op.label || ''}`;
    }
    if (op.type === 'move-entity') {
        return `move-entity:${op.ref || ''}:${op.x},${op.y}`;
    }
    if (op.type === 'add-arrow' || op.type === 'update-arrow' || op.type === 'remove-arrow') {
        return `${op.type}:${op.from}->${op.to}:${op.label || ''}`;
    }
    return op.type || '';
}

/**
 * Union early-turn markup with an enrichment re-emit. Incoming tags that already
 * exist (same op identity) are skipped; new ops are appended.
 * @param {string|null|undefined} existing
 * @param {string|null|undefined} incoming
 * @returns {string}
 */
export function mergePendingChartMarkup(existing, incoming) {
    const existingMarkup = typeof existing === 'string' ? existing : '';
    const incomingMarkup = typeof incoming === 'string' ? incoming : '';
    if (!incomingMarkup.trim()) {
        return existingMarkup;
    }
    if (!existingMarkup.trim()) {
        return incomingMarkup;
    }
    if (existingMarkup === incomingMarkup) {
        return existingMarkup;
    }

    const seen = new Set();
    const merged = [];
    const addTag = (tag) => {
        const parsed = parseChartMutationMarkup(tag);
        const identity = markupOpIdentity(parsed.ops[0]) || tag;
        if (seen.has(identity)) {
            return;
        }
        seen.add(identity);
        merged.push(tag);
    };
    extractChartOpTags(existingMarkup).forEach(addTag);
    extractChartOpTags(incomingMarkup).forEach(addTag);
    return merged.join('');
}

function resolvePendingChartMarkup(state, payload) {
    const chartMarkupFromAnswer = typeof payload.finalAnswer?.metadata?.chartMarkup === 'string'
        ? payload.finalAnswer.metadata.chartMarkup
        : null;
    if (state.pendingChartMarkup) {
        return state.pendingChartMarkup;
    }
    if (chartMarkupFromAnswer) {
        return chartMarkupFromAnswer;
    }
    const rawFallback = state.streamingAnswerRawText || payload.finalAnswer?.answerMarkdown || '';
    if (!rawFallback.trim()) {
        return null;
    }
    const parsed = parseChartMutationMarkup(rawFallback);
    const rawMarkup = parsed.rawMarkup?.trim();
    return rawMarkup || null;
}

function createInitialState(conversationId = null) {
    return {
        status: AGENTIC_RUN_STATUS.IDLE,
        goal: '',
        runId: null,
        userReaction: null,
        /** Per-step progress — tool name + lifecycle status. Never the raw plan JSON. */
        steps: [],
        /** Brief, human-readable planning/replanning narration (collapsible in the UI). */
        reasoningLog: [],
        finalAnswerText: '',
        /** Live, in-progress preview of the answer's text as `answer-delta` events arrive (see
         * dash-api/services/ai-assistant/src/loop/events.ts) — reset to '' by `answer-started` and
         * appended to by every `answer-delta`. Only ever a PREVIEW: `finalAnswerText` above (set by
         * the terminal `run-completed` event) remains the sole authoritative answer text/citations
         * pairing once a run completes; nothing here is ever treated as final. Empty for a run
         * whose final answer comes from a branch that never streams (llm_analyze-verbatim,
         * get_document_content — see finalize-answer.ts), so the UI simply shows nothing until
         * `run-completed` in those cases, exactly like before this field existed. */
        streamingAnswerText: '',
        /** Unstripped live answer buffer — used so incomplete `<chart-op` fragments can complete
         * across deltas before `streamingAnswerText` is derived via the stripper. */
        streamingAnswerRawText: '',
        citations: [],
        error: null,
        /** Past exchanges for the active thread, oldest-first — see this module's header comment.
         * Each entry is a plain `{id, goal, finalAnswerText, citations, status, error}` snapshot
         * of a turn that is no longer the live/current one. Reset alongside the rest of the state
         * whenever the thread itself changes (`loadConversation`/`startNewConversation`). */
        history: [],
        /** MINSKY-16657: the thread this run belongs/belonged to — `null` until the first
         * `submitGoal` call (or an externally-supplied/loaded id) establishes one. */
        conversationId,
        /** Pending ChartMutationPlan from SSE `chart-mutations-proposed` (or reloaded from
         * `finalAnswer.metadata.chartMutations`). Cleared on reject / successful apply / new goal. */
        pendingChartMutations: null,
        /** Pending raw `<chart-op>` markup from SSE `chart-markup-proposed` (or reloaded from
         * `finalAnswer.metadata.chartMarkup` / conversation message metadata). Pure LLM path only. */
        pendingChartMarkup: null,
        /** `auto` | `confirm` — when `auto`, UI applies markup without PureLlmMutationPreview.
         * Defaults to `confirm` for safety when the server omits the field. */
        pendingChartMutationMode: 'confirm',
        /**
         * When true, pending Pure LLM confirm markup is frozen (read-only) in the live turn —
         * all ops resolved or dismissed. Cleared with pending markup.
         */
        pendingChartMutationFrozen: false,
        /** Op status map for a frozen Pure LLM preview (`applied`/`rejected`/…). */
        pendingChartMutationOpStatuses: null,
        /** Pending Pure LLM `<context-suggest>` offers (metadata or parsed tags). */
        pendingContextSuggestions: [],
        /** User-visible log lines after the analyst applies chart suggestions in this turn. */
        chartApplyLog: [],
        /** Monotonic id bumped on each `submitGoal` — scopes auto-apply consume guards per turn. */
        chartApplyGeneration: 0,
        /** Facet actions from `finalAnswer.metadata.entityFilterActions` (Lane C). */
        pendingEntityFilterActions: null,
    };
}

/**
 * @param {unknown} value
 * @returns {'auto'|'confirm'}
 */
export function normalizeChartMutationMode(value) {
    const mode = String(value || '').trim().toLowerCase();
    // Default confirm: Pure LLM entity/edge mutations need review unless the server says auto.
    return mode === 'auto' ? 'auto' : 'confirm';
}

function generateConversationId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Snapshots the current/live turn's fields into a plain history entry, for `submitGoal` to push
 * onto `history` right before it resets the live-turn fields for the new goal. Returns `null` when
 * there is no live turn to preserve (e.g. the very first `submitGoal` in a thread). */
/** Resolves the agentic run id for one user→assistant exchange so thumbs feedback can POST
 * `/api/runs/:runId/feedback` after a thread reload (messages may omit `runId` even when the
 * snapshot `runs[]` still has it). */
function resolveExchangeRunId(assistantMessage, pendingUserMessage, runs, exchangeIndex) {
    if (typeof assistantMessage?.runId === 'string' && assistantMessage.runId) {
        return assistantMessage.runId;
    }
    const assistantId = assistantMessage?.id;
    const userId = pendingUserMessage?.id;
    const matched = (runs || []).find((run) => (
        (assistantId && run?.outputMessageId === assistantId)
        || (userId && run?.inputMessageId === userId)
    ));
    if (matched?.id || matched?.runId) {
        return matched.id || matched.runId;
    }
    const runAtIndex = (runs || [])[exchangeIndex];
    if (runAtIndex?.id || runAtIndex?.runId) {
        return runAtIndex.id || runAtIndex.runId;
    }
    return null;
}

function reactionFromRun(runs, runId) {
    if (!runId) {
        return null;
    }
    const run = (runs || []).find((entry) => entry?.id === runId || entry?.runId === runId);
    const value = run?.userReaction ?? run?.rating;
    return typeof value === 'string' && value ? value : null;
}

function snapshotCurrentTurn(state) {
    if (!state.goal) {
        return null;
    }
    return {
        id: state.runId || `turn-${state.history.length}-${state.goal.length}-${Date.now()}`,
        runId: state.runId || null,
        userReaction: state.userReaction || null,
        goal: state.goal,
        finalAnswerText: state.finalAnswerText,
        citations: state.citations,
        chartMutations: state.pendingChartMutations || null,
        chartMarkup: state.pendingChartMarkup || null,
        mutationMode: state.pendingChartMarkup
            ? normalizeChartMutationMode(state.pendingChartMutationMode)
            : null,
        mutationPreviewFrozen: Boolean(
            state.pendingChartMutationFrozen
            || (state.pendingChartMarkup && state.pendingChartMutationMode === 'confirm')
        ),
        mutationPreviewOpStatuses: state.pendingChartMutationOpStatuses || null,
        contextSuggestions: state.pendingContextSuggestions || [],
        status: state.status,
        error: state.error,
    };
}

/**
 * Derives every exchange (user goal + raw assistant answer text, oldest-first) from a MINSKY-16656
 * `GET /conversations/:id` snapshot, for `loadConversation` to render a resumed thread's full
 * scrollback through the exact same `finalAnswerText`/`citations` shape a live `run-completed`
 * event produces. The assistant message's persisted `content` IS that same raw `answerMarkdown`
 * string a live run streams (see dash-api/services/ai-assistant/src/persistence/
 * persist-run-completion.ts) — passed straight through, unparsed, so the exact same
 * `AgenticFinalAnswer` markdown+marker rendering path handles it, live or resumed.
 *
 * A user message with no following assistant message yet (the run that answers it never
 * persisted, or is still in flight in another tab) still becomes its own exchange, with empty
 * `finalAnswerText` — the pairing here is purely sequential (user → next assistant), not
 * `runId`-keyed, mirroring how messages are persisted one at a time.
 */
function deriveExchangesFromSnapshot(snapshot) {
    const messages = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
    const runs = Array.isArray(snapshot?.runs) ? snapshot.runs : [];
    const exchanges = [];
    let pendingUserMessage = null;

    const flush = (assistantMessage = null) => {
        if (!pendingUserMessage) {
            return;
        }
        const chartMarkup = typeof assistantMessage?.metadata?.chartMarkup === 'string'
            ? assistantMessage.metadata.chartMarkup
            : null;
        const contextSuggestions = resolveContextSuggestions({
            metadata: assistantMessage?.metadata,
        });
        const runId = resolveExchangeRunId(assistantMessage, pendingUserMessage, runs, exchanges.length);
        const userReaction = (typeof assistantMessage?.userReaction === 'string' && assistantMessage.userReaction)
            || reactionFromRun(runs, runId)
            || null;
        exchanges.push({
            id: pendingUserMessage.id || assistantMessage?.id || `exchange-${exchanges.length}`,
            runId,
            userReaction,
            goal: pendingUserMessage.content || '',
            finalAnswerText: assistantMessage?.content || '',
            citations: assistantMessage?.metadata?.citations || [],
            chartMutations: assistantMessage?.metadata?.chartMutations || null,
            chartMarkup,
            mutationMode: chartMarkup
                ? normalizeChartMutationMode(assistantMessage?.metadata?.mutationMode)
                : null,
            // Reloaded confirm previews are inactive in history. Last-turn freeze/statuses
            // come from persistence when the analyst already applied or dismissed.
            mutationPreviewFrozen: Boolean(chartMarkup),
            mutationPreviewOpStatuses: readPersistedMutationPreview(
                snapshot?.conversation?.id,
                chartMarkup
            )?.opStatuses || null,
            contextSuggestions,
            status: AGENTIC_RUN_STATUS.COMPLETED,
            error: null,
        });
        pendingUserMessage = null;
    };

    messages.forEach((message) => {
        if (message?.role === 'user') {
            flush();
            pendingUserMessage = message;
        } else if (message?.role === 'assistant') {
            flush(message);
        }
    });
    flush();

    return exchanges;
}

function upsertStep(steps, stepId, patch) {
    const index = steps.findIndex((step) => step.id === stepId);
    if (index === -1) {
        return [...steps, {id: stepId, tool: null, status: 'running', error: null, ...patch}];
    }
    const next = steps.slice();
    next[index] = {...next[index], ...patch};
    return next;
}

function appendReasoning(state, id, message) {
    if (!id || !message || state.reasoningLog.some((entry) => entry.id === id)) {
        return state;
    }
    return {
        ...state,
        status: AGENTIC_RUN_STATUS.RUNNING,
        reasoningLog: [...state.reasoningLog, {id, message}],
    };
}

/** Maps one `RunEventEmitter` event (see dash-api/services/ai-assistant/src/loop/events.ts) onto
 * a state patch. Exported for direct unit testing without needing a live stream. */
export function reduceAgenticRunEvent(state, {eventName, payload = {}}) {
    switch (eventName) {
    case 'planning-started':
        return appendReasoning(state, `planning-started-${payload.replanAttempt ?? 0}`,
            payload.replanAttempt > 0
                ? `Re-planning (attempt ${payload.replanAttempt})…`
                : 'Planning…');
    case 'planning-completed': {
        const stepCount = payload.plan?.steps?.length ?? 0;
        return appendReasoning(state, `planning-completed-${payload.replanAttempt ?? 0}`,
            `Plan ready — ${stepCount} step${stepCount === 1 ? '' : 's'}.`);
    }
    case 'context-build-started':
        return appendReasoning(state, 'context-build-started', 'Gathering what is on the chart…');
    case 'fintel-loaded':
        return appendReasoning(state, 'fintel-loaded', 'Reading linked reports…');
    case 'kb-dossiers-loaded':
        return payload.entityCount > 0
            ? appendReasoning(state, 'kb-dossiers-loaded', 'Loading known profiles…')
            : state;
    case 'search-round-started': {
        const names = formatSearchQueries(payload.queries);
        return appendReasoning(
            state,
            `search-round-${payload.round ?? 0}`,
            names ? `Searching for ${names}…` : 'Searching…');
    }
    case 'search-round-completed':
        return payload.hitsFound > 0
            ? appendReasoning(
                state,
                `search-round-done-${payload.round ?? 0}`,
                payload.hitsFound === 1 ? 'Found 1 match.' : `Found ${payload.hitsFound} matches.`)
            : state;
    case 'progress': {
        const message = labelForProgressPhase(payload.phase);
        return message ? appendReasoning(state, `progress-${payload.phase}`, message) : state;
    }
    case 'step-started':
        return {
            ...state,
            steps: upsertStep(state.steps, payload.step?.id, {tool: payload.step?.tool, status: 'running', error: null}),
        };
    case 'step-completed':
        return {
            ...state,
            steps: upsertStep(state.steps, payload.step?.id, {tool: payload.step?.tool, status: 'succeeded', error: null}),
        };
    case 'step-failed':
        return {
            ...state,
            steps: upsertStep(state.steps, payload.step?.id, {
                tool: payload.step?.tool,
                status: 'failed',
                error: payload.result?.error || 'Step failed',
            }),
        };
    case 'replanning-triggered':
        return appendReasoning(
            state,
            `replanning-${payload.replanAttempt ?? state.reasoningLog.length}`,
            payload.reason || 'Re-planning…');
    case 'answer-started':
        return {...state, streamingAnswerText: '', streamingAnswerRawText: ''};
    case 'answer-delta': {
        const raw = (state.streamingAnswerRawText || '') + (payload.delta || '');
        return {
            ...state,
            streamingAnswerRawText: raw,
            streamingAnswerText: stripChartOpsFromStreamingText(raw),
        };
    }
    case 'answer-completed':
        return state;
    case 'chart-mutations-proposed': {
        // Payload is the ChartMutationPlan itself (schemaVersion, chartId, summary, mutations, warnings).
        if (!payload || typeof payload !== 'object') {
            return state;
        }
        return {
            ...state,
            pendingChartMutations: payload,
        };
    }
    case 'chart-markup-proposed': {
        // Pure LLM path — raw `<chart-op>` markup string (payload.chartMarkup or bare string).
        const markup = typeof payload?.chartMarkup === 'string'
            ? payload.chartMarkup
            : (typeof payload === 'string' ? payload : null);
        if (!markup || !markup.trim()) {
            return state;
        }
        const mutationMode = normalizeChartMutationMode(
            payload?.mutationMode ?? payload?.metadata?.mutationMode
        );
        // Same-turn enrichment: merge into the live confirm session. Do not unfreeze
        // or wipe statuses — a second leftover dialog is the failure mode.
        if (state.pendingChartMarkup) {
            return {
                ...state,
                pendingChartMarkup: mergePendingChartMarkup(state.pendingChartMarkup, markup),
                pendingChartMutationMode: mutationMode,
            };
        }
        return {
            ...state,
            pendingChartMarkup: markup,
            pendingChartMutationMode: mutationMode,
            pendingChartMutationFrozen: false,
            pendingChartMutationOpStatuses: null,
        };
    }
    case 'run-completed': {
        const hasFinalAnswerPayload = Object.prototype.hasOwnProperty.call(payload, 'finalAnswer');
        const chartMutationsFromAnswer = payload.finalAnswer?.metadata?.chartMutations || null;
        const entityFilterActionsFromAnswer = Array.isArray(
            payload.finalAnswer?.metadata?.entityFilterActions
        )
            ? payload.finalAnswer.metadata.entityFilterActions
            : null;
        const mutationModeFromAnswer = normalizeChartMutationMode(
            payload.finalAnswer?.metadata?.mutationMode
        );
        if (!hasFinalAnswerPayload) {
            // Java backend: first run-completed fires after tool execution, before answer synthesis.
            return {
                ...state,
                status: AGENTIC_RUN_STATUS.RUNNING,
            };
        }
        const pendingChartMarkup = resolvePendingChartMarkup(state, payload);
        let pendingChartMutationMode = state.pendingChartMutationMode === 'auto'
            ? 'auto'
            : (mutationModeFromAnswer || state.pendingChartMutationMode || 'confirm');
        if (markupHasRemoveOps(pendingChartMarkup)) {
            pendingChartMutationMode = 'confirm';
        }
        const contextSuggestionsFromAnswer = resolveContextSuggestions({
            metadata: payload.finalAnswer?.metadata,
            rawText: state.streamingAnswerRawText || payload.finalAnswer?.answerMarkdown,
        });
        const pendingContextSuggestions = state.pendingContextSuggestions?.length
            ? state.pendingContextSuggestions
            : contextSuggestionsFromAnswer;
        return {
            ...state,
            status: AGENTIC_RUN_STATUS.COMPLETED,
            finalAnswerText: payload.finalAnswer?.answerMarkdown || '',
            streamingAnswerText: '',
            streamingAnswerRawText: '',
            citations: payload.finalAnswer?.citations || [],
            // Prefer a live `chart-mutations-proposed` plan; fall back to metadata for reload parity.
            pendingChartMutations: state.pendingChartMutations || chartMutationsFromAnswer,
            // Prefer a live `chart-markup-proposed` string; fall back to metadata for reload parity.
            pendingChartMarkup,
            pendingChartMutationMode,
            pendingChartMutationFrozen: pendingChartMarkup
                ? Boolean(state.pendingChartMutationFrozen)
                : false,
            pendingChartMutationOpStatuses: pendingChartMarkup
                ? state.pendingChartMutationOpStatuses
                : null,
            pendingContextSuggestions,
            pendingEntityFilterActions: entityFilterActionsFromAnswer
                || state.pendingEntityFilterActions
                || null,
            // Live tool steps are only for the in-flight turn.
            steps: [],
            // Do NOT clear chartApplyLog here — auto-apply often finishes after this event, and
            // wiping would hide "Applied N…" / apply failures while the narrator already claimed success.
        };
    }
    default:
        return state;
    }
}

/**
 * Captures the page URL plus optional chart / document grounding for `POST /api/runs`
 * (`startAgenticRun`'s `context` — see request-context.ts). Guarded for non-browser
 * environments (SSR, Jest/jsdom without `window.location`) — returns `undefined` when the URL
 * cannot be determined and no chart/documents were supplied.
 *
 * @param {{
 *   chart?: {chartId: string, title?: string, linkedProducts?: Array<object>}|null,
 *   documents?: Array<{documentId?: string, documentTitle?: string}>,
 * }} [extras]
 * @returns {{url?: string, chart?: {chartId: string, title: string, linkedProducts?: Array<object>}, documents: Array<object>}|undefined}
 */
export function captureRequestContext(extras = {}) {
    const {chart = null, documents = []} = extras;
    let url;
    if (typeof window !== 'undefined' && window.location?.pathname) {
        const {pathname, search} = window.location;
        url = `${pathname}${search || ''}`;
    }

    const urlChartId = chartIdFromWhiteboardUrl(url);
    const hasChart = Boolean(chart?.chartId || urlChartId);
    const docs = Array.isArray(documents) ? documents : [];
    if (!url && !hasChart && docs.length === 0) {
        return undefined;
    }

    const context = {
        ...(url ? {url} : {}),
        documents: docs,
    };
    if (hasChart) {
        const linkedProducts = Array.isArray(chart?.linkedProducts)
            ? chart.linkedProducts
                .filter((p) => p?.productId || p?.targetId || p?.id)
                .map((p) => ({
                    productId: p.productId || p.targetId || p.id,
                    title: p.title || p.documentTitle || undefined,
                    productType: p.productType || p.documentType || undefined,
                }))
            : [];
        context.chart = {
            chartId: chart?.chartId || urlChartId,
            title: chart?.title || '',
            ...(linkedProducts.length > 0 ? {linkedProducts} : {}),
            ...liveChartTopology(chart?.chartId || urlChartId, chart),
        };
    }
    return context;
}

const WHITEBOARD_PATH = /\/charts\/([^/?#]+)/i;

function chartIdFromWhiteboardUrl(url) {
    if (!url) {
        return null;
    }
    const match = WHITEBOARD_PATH.exec(url);
    return match ? match[1] : null;
}

/**
 * Live canvas cards/edges from the open editor. The persisted whiteboard snapshot
 * lags Apply, so the next "what's missing" run must see this, not Postgres.
 */
export function liveChartTopology(chartId, chartProp = null) {
    if (!chartId) {
        return {};
    }
    let entities = Array.isArray(chartProp?.entities)
        ? chartProp.entities
        : (Array.isArray(chartProp?.chartEntities) ? chartProp.chartEntities : []);
    let connections = Array.isArray(chartProp?.connections)
        ? chartProp.connections
        : (Array.isArray(chartProp?.chartConnections) ? chartProp.chartConnections : []);
    let visualAnnotations = Array.isArray(chartProp?.visualAnnotations)
        ? chartProp.visualAnnotations
        : (Array.isArray(chartProp?.chartVisualAnnotations) ? chartProp.chartVisualAnnotations : []);
    let chartImages = Array.isArray(chartProp?.chartImages)
        ? chartProp.chartImages
        : (Array.isArray(chartProp?.images) ? chartProp.images : []);

    const bridge = useChartAiBridgeStore.getState?.();
    if (bridge?.chartId && bridge.chartId === chartId) {
        if (Array.isArray(bridge.chartEntities) && bridge.chartEntities.length > 0) {
            entities = bridge.chartEntities;
        }
        if (Array.isArray(bridge.chartConnections)) {
            connections = bridge.chartConnections;
        }
        if (Array.isArray(bridge.chartVisualAnnotations)) {
            visualAnnotations = bridge.chartVisualAnnotations;
        }
        if (Array.isArray(bridge.chartImages)) {
            chartImages = bridge.chartImages;
        }
    }

    if (!entities.length && !connections.length && !visualAnnotations.length && !chartImages.length) {
        return {};
    }

    return {
        entities: entities.map((entity) => ({
            entityRef: entity.entityRef || entity.wikidataId || entity.id,
            wikidataId: entity.wikidataId || entity.entityRef || entity.id,
            label: entity.label || entity.name || undefined,
            entityType: entity.entityType || entity.type || undefined,
            description: entity.description || undefined,
            ...(entity.shapeId ? {shapeId: entity.shapeId} : {}),
            ...finiteNumberFields({
                x: entity.x,
                y: entity.y,
                w: entity.w,
                h: entity.h,
            }),
        })).filter((entity) => entity.entityRef),
        connections: connections.map((edge) => ({
            from: edge.from || edge.fromEntityRef,
            to: edge.to || edge.toEntityRef,
            ...(edge.label || edge.relation ? {label: edge.label || edge.relation} : {}),
            ...(edge.arrowShapeId ? {arrowShapeId: edge.arrowShapeId} : {}),
        })).filter((edge) => edge.from && edge.to),
        ...(visualAnnotations.length > 0
            ? {
                visualAnnotations: visualAnnotations.map((annotation) => ({
                    ...(annotation.shapeId ? {shapeId: annotation.shapeId} : {}),
                    ...(annotation.shapeType || annotation.type
                        ? {shapeType: annotation.shapeType || annotation.type}
                        : {}),
                    text: annotation.text || annotation.label || '',
                    ...finiteNumberFields({
                        x: annotation.x,
                        y: annotation.y,
                    }),
                })).filter((annotation) => annotation.text),
            }
            : {}),
        ...(chartImages.length > 0
            ? {
                chartImages: chartImages.map((image) => ({
                    ...(image.shapeId ? {shapeId: image.shapeId} : {}),
                    shapeType: 'image',
                    ...(image.mimeType ? {mimeType: image.mimeType} : {}),
                    ...(image.dataUrl || image.src ? {dataUrl: image.dataUrl || image.src} : {}),
                    ...(Number.isFinite(Number(image.byteSize))
                        ? {byteSize: Math.floor(Number(image.byteSize))}
                        : {}),
                    ...(image.altText ? {altText: image.altText} : {}),
                    ...(image.name ? {name: image.name} : {}),
                    ...finiteNumberFields({
                        x: image.x,
                        y: image.y,
                        w: image.w,
                        h: image.h,
                    }),
                })).filter((image) => image.dataUrl),
            }
            : {}),
    };
}

/**
 * @param {Record<string, unknown>} values
 * @returns {Record<string, number>}
 */
function finiteNumberFields(values) {
    const out = {};
    for (const [key, value] of Object.entries(values)) {
        const n = Number(value);
        if (Number.isFinite(n)) {
            out[key] = n;
        }
    }
    return out;
}

/**
 * @param {{
 *   apiBaseUrl?: string,
 *   conversationId?: string|null,
 *   onConversationChange?: (id: string|null) => void,
 *   chartContext?: {chartId: string, title?: string}|null,
 *   documents?: Array<{documentId?: string, documentTitle?: string}>,
 *   pureLlmEnabled?: boolean|null,
 * }} [options]
 */
export function useAgenticRunController({
    apiBaseUrl,
    conversationId: conversationIdProp = null,
    onConversationChange,
    chartContext = null,
    documents = [],
    pureLlmEnabled: pureLlmEnabledProp = null,
} = {}) {
    const [state, setState] = useState(() => createInitialState(conversationIdProp));
    // Starts `true` when mounting with a non-null `conversationIdProp` so the very first render
    // already shows the loading spinner (not the empty state) — the mount-time load kicked off by
    // the prop-sync effect below hasn't run yet at this point, but the fetch it will perform is
    // already known to be pending.
    const [loadingConversation, setLoadingConversation] = useState(() => Boolean(conversationIdProp));
    const [conversationError, setConversationError] = useState(null);
    /** Effective Pure LLM flag — parent may pass it; otherwise fetched from chart config when on a chart. */
    const [pureLlmEnabled, setPureLlmEnabled] = useState(() => (
        typeof pureLlmEnabledProp === 'boolean' ? pureLlmEnabledProp : false
    ));
    const unsubscribeRef = useRef(null);
    /** Always-current chart/document grounding for `submitGoal` without re-binding the callback. */
    const chartContextRef = useRef(chartContext);
    chartContextRef.current = chartContext;
    const documentsRef = useRef(documents);
    documentsRef.current = documents;
    /** Tracks the conversationId this hook currently considers "current" — whether it arrived via
     * `conversationIdProp`, was generated by `submitGoal`, or was loaded via `loadConversation` —
     * so the prop-sync effect below can tell a caller-driven change (must reload) apart from an
     * echo of a change this hook itself just made (must NOT reload).
     *
     * Bugfix (previously loaded no history for a conversationId supplied at MOUNT time): this
     * must start at `null` — NOT `conversationIdProp` — even though `state.conversationId` above
     * already reflects `conversationIdProp` synchronously. Callers commonly mount with a non-null
     * `conversationIdProp` from the very first render (a persisted thread id, a `?conversationId=`
     * URL param, etc. — see `AIAssistantChatPanel.jsx`'s callers). Starting this ref at that same
     * value made the prop-sync effect below believe the prop had already been "applied" before it
     * ever actually fetched anything, so `loadConversation` only ever ran on a LATER prop change
     * (e.g. switching threads via `ThreadHistory`) and never for the initial thread — the initial
     * mount rendered nothing but empty state, with `history`/`goal`/`finalAnswerText` stuck at
     * `createInitialState`'s empty defaults. Starting at `null` makes the effect's very first run
     * see a mismatch whenever `conversationIdProp` is non-null, so it now fetches on mount too. */
    const appliedConversationIdRef = useRef(null);
    /** Monotonic turn id — stale auto-apply work-log callbacks from a prior turn are ignored. */
    const chartApplyGenerationRef = useRef(0);

    const disconnect = useCallback(() => {
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
    }, []);

    useEffect(() => disconnect, [disconnect]);

    // Sync parent-supplied Pure LLM flag, or fetch chart config when on a whiteboard.
    useEffect(() => {
        if (typeof pureLlmEnabledProp === 'boolean') {
            setPureLlmEnabled(pureLlmEnabledProp);
            return undefined;
        }
        if (!chartContext?.chartId) {
            setPureLlmEnabled(false);
            return undefined;
        }
        let cancelled = false;
        getChartAiConfig(apiBaseUrl)
            .then((config) => {
                if (!cancelled) {
                    setPureLlmEnabled(Boolean(config?.pureLlmEnabled));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPureLlmEnabled(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [apiBaseUrl, chartContext?.chartId, pureLlmEnabledProp]);

    const loadConversation = useCallback(async (id) => {
        disconnect();
        appliedConversationIdRef.current = id || null;

        if (!id) {
            setConversationError(null);
            setState(createInitialState(null));
            return;
        }

        setLoadingConversation(true);
        setConversationError(null);
        try {
            const snapshot = await getAgenticConversation(id, apiBaseUrl);
            if (appliedConversationIdRef.current !== id) {
                return;
            }
            // Every exchange but the last becomes scrollback `history`; the last one becomes the
            // live-turn fields (same COMPLETED shape a live `run-completed` event produces) so
            // existing consumers of `goal`/`finalAnswerText`/`citations` keep working unchanged.
            const exchanges = deriveExchangesFromSnapshot(snapshot);
            const lastExchange = exchanges[exchanges.length - 1] || null;
            setState({
                ...createInitialState(id),
                history: exchanges.slice(0, -1),
                goal: lastExchange?.goal || '',
                runId: lastExchange?.runId || null,
                userReaction: lastExchange?.userReaction || null,
                finalAnswerText: lastExchange?.finalAnswerText || '',
                citations: lastExchange?.citations || [],
                pendingChartMutations: lastExchange?.chartMutations || null,
                pendingChartMarkup: lastExchange?.chartMarkup || null,
                pendingChartMutationMode: lastExchange?.chartMarkup
                    ? normalizeChartMutationMode(lastExchange.mutationMode)
                    : 'confirm',
                // Last-turn confirm stays actionable unless this browser already applied
                // or dismissed (persisted statuses). Blind-freezing painted every row
                // Skipped when the analyst never reviewed.
                pendingChartMutationFrozen: Boolean(
                    readPersistedMutationPreview(id, lastExchange?.chartMarkup)?.frozen
                ),
                pendingChartMutationOpStatuses: lastExchange?.mutationPreviewOpStatuses || null,
                pendingContextSuggestions: normalizeContextSuggestions(
                    lastExchange?.contextSuggestions
                ),
                status: lastExchange ? AGENTIC_RUN_STATUS.COMPLETED : AGENTIC_RUN_STATUS.IDLE,
            });
        } catch (error) {
            if (appliedConversationIdRef.current !== id) {
                return;
            }
            // 404 (gone / never persisted) and 502/503/504 (Caddy upstream down during dash-api
            // restart) should not leave a sticky banner on a persisted localStorage thread id.
            if (isRecoverableConversationLoadError(error)) {
                appliedConversationIdRef.current = null;
                setConversationError(null);
                setState(createInitialState(null));
                onConversationChange?.(null);
                return;
            }
            setConversationError(error?.message || 'Failed to load conversation');
            setState(createInitialState(id));
        } finally {
            if (appliedConversationIdRef.current === id || appliedConversationIdRef.current === null) {
                setLoadingConversation(false);
            }
        }
    }, [apiBaseUrl, disconnect, onConversationChange]);

    // Reload when the CALLER-controlled `conversationId` prop changes to a value this hook did
    // not itself just apply — e.g. the user picks a different thread in `ThreadHistory`.
    useEffect(() => {
        if (appliedConversationIdRef.current === conversationIdProp) {
            return;
        }
        loadConversation(conversationIdProp || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationIdProp]);

    const submitGoal = useCallback(async (rawGoal) => {
        const goal = (rawGoal || '').trim();
        if (!goal) {
            return;
        }

        disconnect();
        chartApplyGenerationRef.current += 1;
        const chartApplyGeneration = chartApplyGenerationRef.current;
        const previousConversationId = appliedConversationIdRef.current;
        const conversationId = previousConversationId || generateConversationId();
        appliedConversationIdRef.current = conversationId;
        setConversationError(null);
        setState((current) => {
            const isSameThread = Boolean(previousConversationId);
            const previousTurn = isSameThread ? snapshotCurrentTurn(current) : null;
            const history = previousTurn ? [...current.history, previousTurn] : (isSameThread ? current.history : []);
            return {
                ...createInitialState(conversationId),
                history,
                goal,
                status: AGENTIC_RUN_STATUS.STARTING,
                chartApplyGeneration,
            };
        });

        try {
            const requestContext = captureRequestContext({
                chart: chartContextRef.current,
                documents: documentsRef.current,
            });
            const {runId} = await startAgenticRun(goal, apiBaseUrl, conversationId, requestContext);
            // Persist the thread id only after the server accepts the run — otherwise a failed
            // POST (e.g. 502 while dash-api restarts) would leave a localStorage id that later
            // fails GET /conversations/:id with another sticky error.
            if (conversationId !== previousConversationId) {
                onConversationChange?.(conversationId);
            }
            setState((current) => ({...current, runId, status: AGENTIC_RUN_STATUS.RUNNING}));

            unsubscribeRef.current = streamAgenticRun(runId, {
                apiBaseUrl,
                onEvent: ({eventName, payload}) => {
                    setState((current) => reduceAgenticRunEvent(current, {eventName, payload}));
                },
                onError: (error) => {
                    const conversationIdForReload = appliedConversationIdRef.current;
                    const fail = () => setState((current) => ({
                        ...current,
                        status: AGENTIC_RUN_STATUS.FAILED,
                        error: error?.message || 'Stream error',
                        steps: [],
                        chartApplyLog: [],
                    }));
                    // Backend often finishes after a proxy drop; recover the persisted turn
                    // instead of leaving a raw "Failed to fetch" with no suggestions.
                    if (!conversationIdForReload) {
                        fail();
                        return;
                    }
                    void getAgenticConversation(conversationIdForReload, apiBaseUrl)
                        .then((snapshot) => {
                            if (appliedConversationIdRef.current !== conversationIdForReload) {
                                return;
                            }
                            const exchanges = deriveExchangesFromSnapshot(snapshot);
                            const lastExchange = exchanges[exchanges.length - 1] || null;
                            if (!lastExchange?.finalAnswerText && !lastExchange?.chartMarkup) {
                                fail();
                                return;
                            }
                            setState({
                                ...createInitialState(conversationIdForReload),
                                history: exchanges.slice(0, -1),
                                goal: lastExchange.goal || '',
                                runId: lastExchange.runId || null,
                                userReaction: lastExchange.userReaction || null,
                                finalAnswerText: lastExchange.finalAnswerText || '',
                                citations: lastExchange.citations || [],
                                pendingChartMutations: lastExchange.chartMutations || null,
                                pendingChartMarkup: lastExchange.chartMarkup || null,
                                pendingChartMutationMode: lastExchange.chartMarkup
                                    ? normalizeChartMutationMode(lastExchange.mutationMode)
                                    : 'confirm',
                                pendingChartMutationFrozen: Boolean(
                                    readPersistedMutationPreview(
                                        conversationIdForReload,
                                        lastExchange.chartMarkup
                                    )?.frozen
                                ),
                                pendingChartMutationOpStatuses:
                                    lastExchange.mutationPreviewOpStatuses || null,
                                pendingContextSuggestions: normalizeContextSuggestions(
                                    lastExchange.contextSuggestions
                                ),
                                status: AGENTIC_RUN_STATUS.COMPLETED,
                            });
                        })
                        .catch(fail);
                },
                onEnd: () => {
                    setState((current) => {
                        if (current.status === AGENTIC_RUN_STATUS.COMPLETED
                                || current.finalAnswerText
                                || current.streamingAnswerText) {
                            return current;
                        }
                        const conversationIdForReload = appliedConversationIdRef.current;
                        if (conversationIdForReload) {
                            void loadConversation(conversationIdForReload);
                        }
                        return current;
                    });
                },
            });
        } catch (error) {
            if (!previousConversationId) {
                appliedConversationIdRef.current = null;
                setState((current) => ({
                    ...createInitialState(null),
                    history: current.history,
                    goal: current.goal,
                    status: AGENTIC_RUN_STATUS.FAILED,
                    error: error?.message || 'Failed to start run',
                }));
            } else {
                setState((current) => ({...current, status: AGENTIC_RUN_STATUS.FAILED, error: error?.message || 'Failed to start run'}));
            }
        }
    }, [apiBaseUrl, disconnect, loadConversation, onConversationChange]);

    /** Clears the current turn AND detaches from the current thread (a brand-new conversationId
     * will be minted on the next `submitGoal`) — used by "New chat". */
    const startNewConversation = useCallback(() => {
        disconnect();
        appliedConversationIdRef.current = null;
        setConversationError(null);
        setState(createInitialState(null));
        onConversationChange?.(null);
    }, [disconnect, onConversationChange]);

    /** Clears the LIVE turn but keeps the active conversationId and its scrollback `history` —
     * used internally before starting a fresh run within the SAME thread. Unlike `submitGoal`,
     * this does NOT snapshot the current turn into `history` first (callers that want that should
     * go through `submitGoal`). */
    const reset = useCallback(() => {
        disconnect();
        setState((current) => ({...createInitialState(appliedConversationIdRef.current), history: current.history}));
    }, [disconnect]);

    /**
     * Dismiss pending ChartMutationPlan and/or Pure LLM markup without canvas side effects here.
     * When `markupToClear` is a string, only clears if `pendingChartMarkup` still equals that
     * string — so a stale auto-apply `finally` cannot wipe a newer follow-up proposal.
     *
     * @param {string|undefined} [markupToClear]
     */
    const rejectChartMutations = useCallback((markupToClear) => {
        setState((current) => {
            if (!current.pendingChartMutations && !current.pendingChartMarkup) {
                return current;
            }
            if (typeof markupToClear === 'string'
                && current.pendingChartMarkup
                && current.pendingChartMarkup !== markupToClear) {
                return current;
            }
            return {
                ...current,
                pendingChartMutations: null,
                pendingChartMarkup: null,
                pendingChartMutationMode: 'confirm',
                pendingChartMutationFrozen: false,
                pendingChartMutationOpStatuses: null,
            };
        });
    }, []);

    /**
     * Freeze the live Pure LLM confirm preview after the user finishes (Add all / Dismiss All /
     * every row Added or Skipped). Keeps markup visible and read-only until the next goal.
     *
     * @param {{opStatuses?: Record<string, string>|null}} [detail]
     */
    const freezeChartMutations = useCallback((detail = {}) => {
        setState((current) => {
            if (!current.pendingChartMarkup) {
                return current;
            }
            const nextStatuses = detail?.opStatuses || current.pendingChartMutationOpStatuses;
            if (current.pendingChartMutationFrozen
                && markupOpStatusesEqual(nextStatuses, current.pendingChartMutationOpStatuses)) {
                return current;
            }
            writePersistedMutationPreview(current.conversationId, current.pendingChartMarkup, {
                frozen: true,
                opStatuses: nextStatuses,
            });
            return {
                ...current,
                pendingChartMutationFrozen: true,
                pendingChartMutationOpStatuses: nextStatuses,
            };
        });
    }, []);

    const clearPendingContextSuggestions = useCallback(() => {
        setState((current) => (
            current.pendingContextSuggestions?.length
                ? {...current, pendingContextSuggestions: []}
                : current
        ));
    }, []);

    /**
     * Link a context-suggest offer — submits an affirmative goal so the server runs
     * PureLlmContextExpansionService.applyAccepts on the next turn.
     */
    const linkContextSuggestion = useCallback(async (suggestion) => {
        const goal = buildContextSuggestLinkGoal(suggestion);
        setState((current) => ({
            ...current,
            pendingContextSuggestions: (current.pendingContextSuggestions || [])
                .filter((item) => !(
                    item?.id === suggestion?.id
                    && item?.type === suggestion?.type
                )),
        }));
        await submitGoal(goal);
    }, [submitGoal]);

    /**
     * Dismiss a context-suggest offer — submits a decline goal so the server records
     * declinedContextExpansions via PureLlmContextExpansionService.applyDeclines.
     */
    const dismissContextSuggestion = useCallback(async (suggestion) => {
        const goal = buildContextSuggestDismissGoal(suggestion);
        setState((current) => ({
            ...current,
            pendingContextSuggestions: (current.pendingContextSuggestions || [])
                .filter((item) => !(
                    item?.id === suggestion?.id
                    && item?.type === suggestion?.type
                )),
        }));
        await submitGoal(goal);
    }, [submitGoal]);

    const dismissAllContextSuggestions = useCallback(async () => {
        let items = [];
        setState((current) => {
            items = [...(current.pendingContextSuggestions || [])];
            if (!items.length) {
                return current;
            }
            return {...current, pendingContextSuggestions: []};
        });
        if (!items.length) {
            return;
        }
        // Decline all with a bare "no" against the last offered set (server resolves from metadata).
        await submitGoal('no');
    }, [submitGoal]);

    /** Append user-visible lines when chart suggestions are applied in the assistant panel. */
    const logChartApply = useCallback((entries) => {
        const generation = chartApplyGenerationRef.current;
        const messages = (Array.isArray(entries) ? entries : [entries])
            .filter((entry) => typeof entry === 'string' && entry.trim());
        if (!messages.length) {
            return;
        }
        setState((current) => {
            if (generation !== chartApplyGenerationRef.current) {
                return current;
            }
            return {
                ...current,
                chartApplyLog: [
                    ...current.chartApplyLog,
                    ...messages.map((message, index) => ({
                        id: `chart-apply-${current.chartApplyLog.length + index}-${Date.now()}`,
                        message,
                    })),
                ],
            };
        });
    }, []);

    const setUserReaction = useCallback((userReaction) => {
        setState((current) => (
            current.userReaction === userReaction ? current : {...current, userReaction}
        ));
    }, []);

    return useMemo(() => ({
        ...state,
        pureLlmEnabled,
        loadingConversation,
        conversationError,
        submitGoal,
        reset,
        startNewConversation,
        loadConversation,
        setUserReaction,
        rejectChartMutations,
        freezeChartMutations,
        clearPendingChartMutations: rejectChartMutations,
        rejectChartMarkup: rejectChartMutations,
        clearPendingChartMarkup: rejectChartMutations,
        clearPendingContextSuggestions,
        linkContextSuggestion,
        dismissContextSuggestion,
        dismissAllContextSuggestions,
        logChartApply,
    }), [
        state,
        pureLlmEnabled,
        loadingConversation,
        conversationError,
        submitGoal,
        reset,
        startNewConversation,
        loadConversation,
        setUserReaction,
        rejectChartMutations,
        freezeChartMutations,
        clearPendingContextSuggestions,
        linkContextSuggestion,
        dismissContextSuggestion,
        dismissAllContextSuggestions,
        logChartApply,
    ]);
}

export default useAgenticRunController;
